import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import FilterBar, { type FilterGroup } from "@/pages/category/components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaList } from "@/mocks/media";
import {
  categoryMetaList,
  genreOptions,
  popularityOptions,
  regionOptions,
  yearOptions,
} from "@/mocks/categories";

const PER_PAGE = 20;

type SortKey = "latest" | "hot" | "rating";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "hot", label: "最热", icon: "ri-fire-line" },
  { key: "rating", label: "评分", icon: "ri-star-line" },
];

const hotWeight = (tags: string[]) => {
  if (tags.includes("热门")) return 3;
  if (tags.includes("口碑佳作")) return 2;
  if (tags.includes("新上线")) return 1;
  return 0;
};

export default function CategoryPage() {
  const { type } = useParams();
  const meta = categoryMetaList.find((item) => item.key === type);
  const isMediaCategory = type === "movie" || type === "tv";

  usePageMeta(
    meta ? `${meta.name} - 蜜桃传媒` : "影视分类 - 蜜桃传媒",
    meta
      ? `${meta.desc} 蜜桃传媒为你整理${meta.name}分类下的优质内容，支持类型、地区、年份与热门程度筛选。`
      : "蜜桃传媒影视分类页，浏览不同类型的影视作品。"
  );

  const [selected, setSelected] = useState<Record<string, string[]>>({
    genre: [],
    region: [],
    year: [],
    popularity: [],
  });
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [type, selected, sort]);

  const baseList = useMemo(
    () => mediaList.filter((item) => item.type === type),
    [type]
  );

  const filtered = useMemo(() => {
    let result = baseList.filter((item) => {
      const genreOk =
        selected.genre.length === 0 || item.genres.some((g) => selected.genre.includes(g));
      const regionOk =
        selected.region.length === 0 || selected.region.includes(item.region);
      const yearOk =
        selected.year.length === 0 || selected.year.includes(String(item.year));
      const popOk =
        selected.popularity.length === 0 ||
        item.tags.some((t) => selected.popularity.includes(t));
      return genreOk && regionOk && yearOk && popOk;
    });

    result = [...result].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "hot") {
        const diff = hotWeight(b.tags) - hotWeight(a.tags);
        return diff !== 0 ? diff : b.rating - a.rating;
      }
      return a.releaseDate < b.releaseDate ? 1 : -1;
    });
    return result;
  }, [baseList, selected, sort]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const groups: FilterGroup[] = [
    { key: "genre", label: "类型", options: genreOptions },
    { key: "region", label: "地区", options: regionOptions },
    { key: "year", label: "年份", options: yearOptions.map(String) },
    { key: "popularity", label: "热门", options: popularityOptions },
  ];

  const handleToggle = (groupKey: string, value: string) => {
    setSelected((prev) => {
      const current = prev[groupKey] || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [groupKey]: next };
    });
  };

  const handleReset = () => {
    setSelected({ genre: [], region: [], year: [], popularity: [] });
    setSort("latest");
  };

  if (!isMediaCategory) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6">
        <Breadcrumb items={[{ label: meta?.name || "影视分类" }]} />
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-information-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-black text-foreground-950">
            该分类请前往对应栏目查看
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            明星相关内容已整合在「明星动态」专栏，欢迎前往浏览。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/star-news"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
            >
              前往明星动态
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: meta?.name || "影视分类" }]} />

      <header className="mt-4 rounded-lg bg-secondary-100 p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${meta?.icon || "ri-movie-line"} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              {meta?.name}
            </h1>
            <p className="text-sm font-medium text-secondary-800">{meta?.enName}</p>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
          {meta?.desc}
        </p>
      </header>

      <div className="mt-6">
        <FilterBar
          groups={groups}
          selected={selected}
          onToggle={handleToggle}
          onReset={handleReset}
          resultCount={filtered.length}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
          {totalPages > 1 && (
            <span className="ml-1 text-foreground-500">
              · 第 {page} / {totalPages} 页
            </span>
          )}
        </p>
        <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSort(option.key)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                sort === option.key
                  ? "bg-background-50 text-primary-700"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className={option.icon}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {paged.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {paged.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-movie-line text-3xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-black text-foreground-950">
            暂无符合条件的影视内容
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            试着放宽筛选条件，或重置筛选查看全部内容。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      )}

      <Pagination current={page} total={totalPages} onChange={setPage} />
    </div>
  );
}